/**
 * IrisOverlay — full-height Blueprint <Drawer> that mounts IrisChat, docked
 * immediately to the RIGHT of the left nav rail (not over it).
 *
 * Toggled by the rail's IRIS entry, the case workspace "Ask IRIS" action, and
 * the global ⌘⇧U hotkey (registered in AppShell via useHotkeys). Built on
 * Blueprint's <Drawer position="left"> — idiomatic, full-height, focus-managed,
 * portaled. The drawer is portaled to <body> (outside .bp-app), so we offset
 * its `left` by the live rail width — `railCollapsed` selects the collapsed vs
 * expanded token since the .bp-app-scoped override can't reach the portal. No
 * backdrop so the workspace stays visible/usable behind it (Foundry AIP-Assist
 * docking); Esc, the header X, or ⌘⇧U dismiss it. The chat is mounted only
 * while open (Drawer unmounts its children on close) so the model doesn't tick
 * away in the background.
 */

import type { ReactElement } from "react";
import { Drawer } from "@blueprintjs/core";

import { IrisChat } from "./iris-chat";
import type { CaseKnowledge } from "../../lib/iris-case-answers";

interface Props {
  open: boolean;
  onClose: () => void;
  /**
   * Whether the left nav rail is collapsed (icon-only). Drives the drawer's
   * left offset so it stays flush against the rail's right edge in both states.
   */
  railCollapsed?: boolean;
  /** Active case anchor — forwarded to IrisChat as system-prompt context. */
  caseId?: string;
  caseTitle?: string;
  /**
   * Active case knowledge-graph slice. When supplied, IRIS answers the spec
   * example queries case-bounded (CAP 6) before any Bedrock fallback. The host
   * (case workspace) threads this in when opening IRIS from a case.
   */
  knowledge?: CaseKnowledge;
}

export function IrisOverlay({ open, onClose, railCollapsed, caseId, caseTitle, knowledge }: Props): ReactElement {
  // Flush against the rail's right edge. Tokens live at :root so the portaled
  // drawer can read them (the .is-rail-collapsed override is .bp-app-scoped).
  const railOffset = railCollapsed
    ? "var(--bp-rail-collapsed-w, 56px)"
    : "var(--bp-rail-w, 230px)";
  return (
    <Drawer
      data-testid="iris-overlay"
      className="bp-iris-drawer"
      isOpen={open}
      onClose={onClose}
      position="left"
      size={420}
      // Pop into position instantly — no slide. The CSS (.bp-iris-drawer)
      // cancels the overlay-enter transform; this stops Blueprint holding the
      // element for a 300ms enter/exit so mount/unmount is immediate too.
      transitionDuration={0}
      style={{ left: railOffset }}
      hasBackdrop={false}
      canOutsideClickClose={false}
      aria-label="IRIS Assistant panel"
    >
      <IrisChat onClose={onClose} caseId={caseId} caseTitle={caseTitle} knowledge={knowledge} />
    </Drawer>
  );
}
